/**
 * Block-nesting indentation rules for DATA(Q) Query scripts.
 *
 * Pure string logic, kept out of QueryIndentExtension so it can be unit
 * tested without a ProseMirror document. The keyword sets mirror the engine's
 * script preprocessor (QueryProcess.cpp `InitialiseKeywords`), which matches
 * them case-sensitively in upper case.
 */

/** Columns one nesting level adds. */
export const INDENT_SIZE = 2

/** The literal one level of indentation is written with. */
export const INDENT_UNIT = " ".repeat(INDENT_SIZE)

// Openers indent the lines that follow them; closers dedent their own line;
// ELSE does both — it sits at the opener's level and re-indents its body.
const OPENERS = new Set(["FOR", "IF", "IFDEF", "IFNDEF"])
const CLOSERS = new Set(["ENDFOR", "ENDIF"])
const MIDDLES = new Set(["ELSE"])

// `(?!:)` keeps the filter clause `IF: name` out — that is a command
// parameter, not a preprocessor conditional.
const LINE_KEYWORD = /^[ \t]*([A-Z]+)\b(?!:)/

/**
 * The upper-case word a line leads with, or null when the line starts with
 * anything else (a comment, a mixed-case word, `IF:`, nothing at all).
 */
export const leadingKeyword = (line: string): string | null =>
  LINE_KEYWORD.exec(line)?.[1] ?? null

/** Leading whitespace of a line, in columns (a tab counts as one). */
export const lineIndentWidth = (line: string): number =>
  line.length - line.trimStart().length

/**
 * Nesting depth for each line, from the block structure alone.
 * Depth never drops below zero, so unmatched closers clamp instead of
 * dragging the rest of the script into negative indentation.
 */
export const computeIndentDepths = (lines: string[]): number[] => {
  let depth = 0
  return lines.map((line) => {
    const keyword = leadingKeyword(line)
    const dedentsOwnLine =
      keyword !== null && (CLOSERS.has(keyword) || MIDDLES.has(keyword))
    const ownDepth = dedentsOwnLine ? Math.max(0, depth - 1) : depth

    if (keyword !== null && CLOSERS.has(keyword)) {
      depth = Math.max(0, depth - 1)
    } else if (keyword !== null && OPENERS.has(keyword)) {
      depth += 1
    }
    return ownDepth
  })
}

/**
 * Re-indent a whole script from its block nesting. Blank lines come back
 * empty rather than carrying whitespace nobody can see.
 */
export const formatQueryIndentation = (lines: string[]): string[] => {
  const depths = computeIndentDepths(lines)
  return lines.map((line, index) => {
    const body = line.trim()
    return body ? INDENT_UNIT.repeat(depths[index]) + body : ""
  })
}

/**
 * Indent width a line inserted directly below `line` should start at: the
 * line's own indent, one level deeper when it opens a block.
 */
export const indentWidthForNextLine = (line: string): number => {
  const keyword = leadingKeyword(line)
  const current = lineIndentWidth(line)
  return keyword !== null && (OPENERS.has(keyword) || MIDDLES.has(keyword))
    ? current + INDENT_SIZE
    : current
}

/** How many leading whitespace columns one outdent should remove. */
export const outdentWidth = (line: string): number =>
  Math.min(INDENT_SIZE, lineIndentWidth(line))

/** True when the line's first word closes or splits a block. */
export const isBlockCloser = (keyword: string): boolean =>
  CLOSERS.has(keyword) || MIDDLES.has(keyword)
